import React, { useState } from 'react';
import RazorpayPaymentButton from '../components/RazorpayPaymentButton';
import PaymentNotification from '../components/PaymentNotification';
import { RAZORPAY_CONFIG } from '../config/razorpay';

const PaymentTestPage = ({ darkMode }) => {
  const [paymentAmount, setPaymentAmount] = useState(RAZORPAY_CONFIG.AMOUNTS.MBBS_SEAT_BOOKING);
  const [notification, setNotification] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: 'Test User',
    email: 'test@example.com',
    phone: '9999999999'
  });

  const handlePaymentSuccess = (paymentData) => {
    console.log('✅ Payment Success:', paymentData);
    
    // Show beautiful success notification
    setNotification({
      type: 'success',
      title: 'Test Payment Successful!',
      message: 'The Razorpay integration is working perfectly. Payment has been processed successfully.',
      paymentId: paymentData.razorpay_payment_id
    });
  };

  const handlePaymentFailure = (error) => {
    console.error('❌ Payment Failed:', error);
    
    // Show beautiful error notification
    setNotification({
      type: 'error',
      title: 'Test Payment Failed',
      message: error.error || error.description || 'The test payment could not be processed. This is normal in development environment.'
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Temporary test function to show success notification
  const showTestSuccessNotification = () => {
    setNotification({
      type: 'success',
      title: 'Test Payment Successful!',
      message: 'The Razorpay integration is working perfectly. Payment has been processed successfully.',
      paymentId: 'pay_test123456789' // Fake payment ID for testing
    });
  };

  // Temporary test function to show failure notification
  const showTestFailureNotification = () => {
    setNotification({
      type: 'error',
      title: 'Test Payment Failed!',
      message: 'The test payment could not be processed. This is normal in development environment.',
      paymentId: null
    });
  };

  const amountOptions = [
    { label: '₹500 - Consultation Fee', value: RAZORPAY_CONFIG.AMOUNTS.CONSULTATION_FEE },
    { label: '₹1,000 - Application Fee', value: RAZORPAY_CONFIG.AMOUNTS.APPLICATION_FEE },
    { label: '₹20,000 - MBBS Seat Booking', value: RAZORPAY_CONFIG.AMOUNTS.MBBS_SEAT_BOOKING },
  ];

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'} py-8`}>
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">🧪 Razorpay Payment Test</h1>
          <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Test the integrated Razorpay payment gateway
          </p>
        </div>

        {/* Configuration Display */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl border p-6 mb-6`}>
          <h2 className="text-xl font-semibold mb-4">🔧 Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Key ID:</strong>
              <div className="font-mono bg-gray-100 dark:bg-gray-700 p-2 rounded mt-1 break-all">
                {RAZORPAY_CONFIG.key_id}
              </div>
            </div>
            <div>
              <strong>Company:</strong>
              <div className="mt-1">{RAZORPAY_CONFIG.company_name}</div>
            </div>
            <div>
              <strong>Currency:</strong>
              <div className="mt-1">{RAZORPAY_CONFIG.currency}</div>
            </div>
            <div>
              <strong>Theme Color:</strong>
              <div className="flex items-center gap-2 mt-1">
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: RAZORPAY_CONFIG.theme_color }}
                ></div>
                {RAZORPAY_CONFIG.theme_color}
              </div>
            </div>
          </div>
        </div>

        {/* Test Form */}
        <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl border p-6 mb-6`}>
          <h2 className="text-xl font-semibold mb-4">💳 Test Payment</h2>
          
          {/* Amount Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Payment Amount:</label>
            <select
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(parseInt(e.target.value))}
              className={`w-full p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              {amountOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">Name:</label>
              <input
                type="text"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                className={`w-full p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email:</label>
              <input
                type="email"
                value={customerInfo.email}
                onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                className={`w-full p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Phone:</label>
              <input
                type="tel"
                value={customerInfo.phone}
                onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                className={`w-full p-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
          </div>

          {/* Payment Button */}
          <RazorpayPaymentButton
            amount={paymentAmount}
            description="Test Payment"
            userInfo={customerInfo}
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
            buttonText="Test Pay"
          />
        </div>

        {/* Test Cards Info */}
        <div className={`${darkMode ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-200'} rounded-2xl border p-6`}>
          <h3 className="text-lg font-semibold mb-3 text-blue-600">🧪 Test Card Details</h3>
          <p className="text-sm mb-3">Use these test card details in Razorpay's test mode:</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <strong>Success Card:</strong>
              <div className="font-mono mt-1">4111 1111 1111 1111</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">CVV: Any 3 digits, Expiry: Any future date</div>
            </div>
            <div>
              <strong>Failed Card:</strong>
              <div className="font-mono mt-1">4000 0000 0000 0002</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">CVV: Any 3 digits, Expiry: Any future date</div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> These are test credentials. In production, real payments will be processed using your live Razorpay keys.
            </p>
          </div>
        </div>

        {/* TEMPORARY TEST BUTTONS - Remove these later */}
        <div className={`mt-6 ${darkMode ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'} rounded-2xl border p-6`}>
          <h3 className="text-lg font-semibold mb-3 text-purple-600">🧪 Test Modal Notifications</h3>
          <p className="text-sm mb-4">Test the new centered modal notification appearance:</p>
          <div className="flex gap-3">
            <button
              onClick={showTestSuccessNotification}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg text-sm transition-colors"
            >
              ✅ Test Success Modal
            </button>
            <button
              onClick={showTestFailureNotification}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg text-sm transition-colors"
            >
              ❌ Test Failure Modal
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className={`mt-6 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl border p-6`}>
          <h3 className="text-lg font-semibold mb-3">📋 Testing Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Select a payment amount from the dropdown</li>
            <li>Fill in customer information (or use the default values)</li>
            <li>Click the "Test Pay" button to open Razorpay checkout</li>
            <li>Use the test card details provided above</li>
            <li>Complete the payment to test success/failure flows</li>
            <li>Check the browser console for detailed payment logs</li>
          </ol>
        </div>
      </div>
      
      {/* Beautiful Payment Notification - Rendered at page level */}
      {notification && (
        <PaymentNotification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          paymentId={notification.paymentId}
          onClose={closeNotification}
          autoClose={true}
          duration={10000} // 10 seconds for test page
        />
      )}
    </div>
  );
};

export default PaymentTestPage;
